// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CloudUploadOutlined } from '@/constants/hardfin-standard-icons';
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChangeEventHandler,
  DragEventHandler,
  ForwardedRef,
  KeyboardEventHandler,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

interface Props {
  isUploading?: boolean;
  multiple?: boolean;
  onFilesSelected: (files: File[]) => void;
  uploadLabel?: string;
  uploadingLabel?: string;
}

export interface AttachmentUploadZoneHandle {
  openFilePicker: () => void;
}

function AttachmentUploadZoneRenderer(
  props: Props,
  ref: ForwardedRef<AttachmentUploadZoneHandle>
) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const uploadLabel = props.uploadLabel ?? 'Upload files';
  const uploadingLabel = props.uploadingLabel ?? 'Uploading...';
  const isDisabled = Boolean(props.isUploading);

  useImperativeHandle(ref, () => ({
    openFilePicker: () => {
      fileInputRef.current?.click();
    },
  }));

  const handleFileInputChange: ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length > 0) {
      props.onFilesSelected(selectedFiles);
    }
    event.target.value = '';
  };

  const handleUploadTargetKeyDown: KeyboardEventHandler<HTMLLabelElement> = (
    event
  ) => {
    if (isDisabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleDragOver: DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    if (!isDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave: DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop: DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (isDisabled) {
      return;
    }

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      props.onFilesSelected(droppedFiles);
    }
  };

  return (
    <Box
      component="label"
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onKeyDown={handleUploadTargetKeyDown}
      sx={{
        border: '2px dashed',
        borderColor: isDragging ? 'primary.main' : 'grey.300',
        borderRadius: 1,
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        cursor: isDisabled ? 'default' : 'pointer',
        display: 'block',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: isDisabled ? 'grey.300' : 'primary.main',
          backgroundColor: isDisabled ? 'transparent' : 'action.hover',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        multiple={props.multiple}
        onChange={handleFileInputChange}
        disabled={isDisabled}
        tabIndex={-1}
        sx={{
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: 1,
          overflow: 'hidden',
          position: 'absolute',
          bottom: 0,
          left: 0,
          whiteSpace: 'nowrap',
          width: 1,
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{
          color: 'primary.main',
          fontWeight: 600,
          padding: 1.5,
          pointerEvents: 'none',
        }}
      >
        {props.isUploading ? (
          <CircularProgress size={16} />
        ) : (
          <CloudUploadOutlined fontSize="small" />
        )}
        <Typography
          variant="button"
          color="primary"
          sx={{ textTransform: 'none' }}
        >
          {props.isUploading ? uploadingLabel : uploadLabel}
        </Typography>
      </Stack>
    </Box>
  );
}

export const AttachmentUploadZone = forwardRef(AttachmentUploadZoneRenderer);

export function AttachmentUploadZoneSkeleton() {
  return (
    <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
  );
}
